import React, { useEffect, useMemo, useState } from "react";
import { publicLink } from "@/lib/landerDomain";
import { Eye } from "@phosphor-icons/react";
import api from "@/lib/api";
import { FUNNEL_OPTIONS } from "@/lib/funnelOptions";

const withPreview = (path) => `${path}${path.includes("?") ? "&" : "?"}preview=1`;

function useMyPages() {
  const [pages, setPages] = useState(null);
  const [firm, setFirm] = useState("");
  useEffect(() => {
    api.get("/buyer/funnel-profiles")
      .then((r) => {
        const profiles = r.data?.profiles || [];
        setFirm((profiles.find((p) => p.firm_name)?.firm_name || "").trim());
        const firm = (p) => (profiles.length > 1 ? `${p.firm_name || p.code} · ` : "");
        setPages(profiles.flatMap((p) => [
          ...(p.links || []).map((l) => ({ key: `s-${p.code}-${l.funnel}`, label: `${firm(p)}${l.title} (your page)`, path: l.path })),
          ...(p.own_pages || []).map((o) => ({ key: `o-${o.slug}`, label: `${firm(p)}${o.name} (your own page)`, path: o.path })),
        ]));
      })
      .catch(() => setPages([]));
  }, []);
  return [pages, firm];
}

function pageSrc(choice, mine, brandOn, brandName) {
  if (choice.startsWith("g-")) {
    const q = new URLSearchParams({ preview: "1", badge: "0" });
    if (brandOn && brandName) {
      q.set("brand", brandName);
      q.set("firm", brandName);
    }
    return `/lp/${choice.slice(2)}?${q}`;
  }
  const m = mine.find((x) => x.key === choice);
  return m ? withPreview(m.path) : "";
}

// Buyer dashboard: their real branded pages, plus the generic pages with an optional brand overlay.
export function BuyerLandingPreview({ brandName: dashboardBrand }) {
  const [mine, profileFirm] = useMyPages();
  // Company name from the dashboard, else from the buyer's own page.
  const brandName = dashboardBrand || profileFirm;
  const [choice, setChoice] = useState("");
  const [brandOn, setBrandOn] = useState(false);
  useEffect(() => {
    if (mine && !choice) setChoice(mine[0]?.key || `g-${FUNNEL_OPTIONS[0].slug}`);
  }, [mine, choice]);
  const generic = choice.startsWith("g-");
  const src = useMemo(() => (mine && choice ? pageSrc(choice, mine, brandOn, brandName) : ""), [mine, choice, brandOn, brandName]);

  return (
    <div className="mt-8 brut-card" data-testid="buyer-landing-preview">
      <div className="p-5 border-b border-black flex items-center gap-2 flex-wrap">
        <Eye size={14} weight="bold" />
        <div className="font-chivo font-black uppercase tracking-tighter">Preview your lead source</div>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <label className="label-mono" htmlFor="preview-vertical-select">Landing page:</label>
          <select id="preview-vertical-select" value={choice} onChange={(e) => setChoice(e.target.value)}
                  data-testid="preview-vertical-select" className="border border-black px-2 py-1 text-xs font-mono max-w-full">
            {mine?.length > 0 && (
              <optgroup label="Your pages (your name, logo and phone)">
                {mine.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </optgroup>
            )}
            <optgroup label="Generic pages (no branding)">
              {FUNNEL_OPTIONS.map((f) => <option key={f.slug} value={`g-${f.slug}`}>{f.title} (generic)</option>)}
            </optgroup>
          </select>
          {generic && (
            <label className={`inline-flex items-center gap-2 border border-black px-2 py-1 ${brandName ? "cursor-pointer hover:bg-zinc-100" : "opacity-60"}`} data-testid="preview-brand-toggle-wrap">
              <input type="checkbox" checked={brandOn} onChange={(e) => setBrandOn(e.target.checked)} disabled={!brandName}
                     data-testid="preview-brand-toggle" className="accent-black" />
              <span className="text-[10px] uppercase tracking-widest font-bold">
                Overlay my brand{brandName ? ` (${brandName})` : " (no company name yet)"}
              </span>
            </label>
          )}
          {src && (
            <a href={publicLink(src)} target="_blank" rel="noreferrer" data-testid="preview-open-newtab"
               className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white ml-auto">
              ↗ Open in new tab
            </a>
          )}
        </div>
        <p className="mb-4 text-[11px] text-zinc-600" data-testid="preview-note">
          {generic
            ? (!brandName ? "A generic page. Ask for your company name under Your company details to see it branded."
              : brandOn ? `A generic page, shown with your company name (${brandName}).`
                : "A generic page. Tick Overlay my brand to see it with your company name.")
            : "Your real page, with your company name, logo and phone, exactly as your leads see it."}
        </p>
        <div className="border-4 border-black bg-zinc-100 overflow-hidden" style={{ height: 780 }}>
          {src && (
            <iframe key={src} src={publicLink(src)} title="Landing page preview" data-testid="preview-iframe"
                    style={{ width: "100%", height: "100%", border: 0, background: "#fff" }}
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups" />
          )}
        </div>
        <div className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
          This is the page your leads see the moment they click an ad. Sub-source tracking (<code className="font-mono">?sub=</code>),
          TCPA consent and the TrustedForm certificate are all captured on a real submission. Previews here are never counted.
        </div>
      </div>
    </div>
  );
}
